import type { ReactNode } from 'react';
import { useState } from 'react';
import { Animated, Platform, Pressable, type ViewStyle } from 'react-native';
import { useEntranceAnimation, useScaleAnimation } from './useAnimations';

interface AnimatedCardProps {
  children: ReactNode;
  delay?: number;
  duration?: number;
  style?: ViewStyle;
  onPress?: () => void;
  enableHover?: boolean;
  enablePress?: boolean;
  hoverGlow?: string;
  hoverBorderColor?: string;
}

export function AnimatedCard({
  children,
  delay = 0,
  duration = 500,
  style,
  onPress,
  enableHover = true,
  enablePress = true,
  hoverGlow,
  hoverBorderColor,
}: Readonly<AnimatedCardProps>) {
  const [isHovered, setIsHovered] = useState(false);

  const { style: entranceStyle } = useEntranceAnimation({
    delay,
    duration,
    translateY: 20,
  });

  const {
    style: scaleStyle,
    onPressIn,
    onPressOut,
  } = useScaleAnimation({
    pressedScale: 0.98,
  });

  const webHoverStyle = Platform.select({
    web: {
      cursor: onPress ? 'pointer' : 'default',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
      ...(isHovered &&
        hoverGlow && {
          boxShadow: `0 0 20px ${hoverGlow}, 0 4px 12px rgba(0, 0, 0, 0.15)`,
        }),
      ...(isHovered &&
        hoverBorderColor && {
          borderColor: hoverBorderColor,
        }),
    } as ViewStyle,
    default: {},
  });

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    },
    default: {},
  });

  if (onPress || enablePress) {
    return (
      <Animated.View
        style={[entranceStyle, scaleStyle, { overflow: 'visible', flexGrow: style?.flexGrow }]}
      >
        <Pressable
          onPress={onPress}
          onPressIn={enablePress ? onPressIn : undefined}
          onPressOut={enablePress ? onPressOut : undefined}
          style={[style, webHoverStyle, { overflow: 'visible' }]}
          {...hoverProps}
        >
          {children}
        </Pressable>
      </Animated.View>
    );
  }

  return (
    <Animated.View style={[entranceStyle, style, { overflow: 'visible' }]}>
      {children}
    </Animated.View>
  );
}
